import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { CSSTransition } from 'react-transition-group';

import renderModal from '@components/show_page/modals/renderModal';
import { pluralizeObject } from '@helpers/show_page_helpers';
import { create as createLink } from '@api/v4/resources/share_manifests';
import {
  destroy as deleteLink,
  get as getLink,
  update as updateLink,
} from '@api/v4/share_manifests';
import InputContainer from '@components/library/input_container/InputContainer';
import DateTimePicker from '@components/library/date_time_picker/BaseDateTimePicker';
import ClipboardCopy from '@components/common/clipboard_copy/main';
import ToggleContainer from '@components/show_page/bulk_actions/sub_components/bulk_share_toggle';
import { BFLoader } from '@components/common/loader/main';
import useDebounce from '@components/common/custom_hooks/useDebounce';

import { reportGA, reportInsight } from '@helpers/insight_shares';

const initialState = {
  // settings, user cannot change
  key: null, // share manifest key
  restrict_shares_setting: false, // BF setting, enforced before_save
  require_share_identification_setting: false, // BF setting, enforced before_save
  link: null,
  // boolean params, user can change, update immediately
  expires: false,
  internal: false, // user can update in bulk share modal
  require_identification: false,
  view_only: false,
  // params, user can change, update on clicking on save
  availability_end: null,
  name: '',
};

const paramNotificationCopyMap = {
  internal: t('Privacy updated'),
  require_identification: t('Email requirement updated'),
  view_only: t('View only mode updated'),
  // expires: t('expiration updated'), notification for avialabilty_end only- these update together
  name: t('Share Link name updated'),
  availability_end: t('Expiration updated'),
  'reset-state': t('Fields restored'),
};

const reducer = (state = initialState, { payload, type }) => {
  switch (type) {
    case 'reset-state': {
      return {
        ...state,
        ...payload,
      };
    }
    case 'internal':
    case 'require_identification':
    case 'view_only':
    case 'expires': {
      const togglingInternalToFalse = (type === 'internal' && state.internal);
      const togglingExpiresToFalse = (type === 'expires' && state.expires);
      return {
        ...state,
        ...{ [type]: !state[type] },
        ...togglingInternalToFalse && { require_identification: false },
        ...togglingExpiresToFalse && { availability_end: null },
      };
    }
    case 'name':
    case 'availability_end': {
      return {
        ...state,
        ...{ [type]: payload },
      };
    }
    default:
      return state;
  }
};

const sweetAlertDeleteOptions = {
  title: t("Delete Share Link"),
  text: t("Are you sure you want to delete?"),
  type: "warning",
  showCancelButton: true,
  cancelButtonText: t("No"),
  confirmButtonText: t("Delete"),
  closeOnConfirm: true,
  customClass: "deleting"
};

const redirectToContextable = () => {
  if (BFG.resource.type === 'collection') {
    // TODO update BFG global to accomodate this situation (when collection AND bf slug are needed)
    window.location.href = `/${BF.fx.brandfolder().slug}/${BFG.resource.slug}`;
  } else if (BFG.resource.type === 'brandfolder') {
    window.location.href = `/${BFG.resource.slug}`;
  } else {
    window.location.href = "/";
  }
};

const showOptionsTransitionTime = 300; // milliseconds
const debounceDelay = 1500; // milliseconds
const editLinkView = !!BFG.manifestDigest;
const defaultExpiration = moment().add(7, 'days').set({ hour: 23, minute: 59, second: 0 }).format('YYYY-MM-DD HH:mm');

const BulkShareModal = ({
  closeModal,
  selectedAssetKeys,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [lockUpdatesForReset, setLockUpdatesForReset] = useState(true);
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  // used for edit link view (e.g. within share link view)
  const [stateForReset, setStateForReset] = useState();
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const debouncedName = useDebounce({ delay: debounceDelay, value: state.name, disableDebounce: isLoading || lockUpdatesForReset });
  const debouncedExpiration = useDebounce({ delay: debounceDelay, value: state.availability_end, disableDebounce: isLoading || lockUpdatesForReset });
  const assetQtyObject = pluralizeObject(selectedAssetKeys);
  const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');
  const assetCopyUppercase = assetQtyObject.plural ? t('Assets') : t('Asset');
  const defaultName = () => (
    `${assetQtyObject.qty} ${assetCopyUppercase} from ${BFG.resource.name}`
  );

  const handleNotify = (type) => {
    Notify.create({
      title: `${t('Share Link Change')}`,
      body: paramNotificationCopyMap[type],
      type: "success"
    });
  };

  const handleReset = ({ payload, disableFetchUpdate = true }) => {
    dispatch({ type: 'reset-state', payload, });

    if (disableFetchUpdate) { // initial handleGet or handleCreate
      setIsLoading(false);
      setLockUpdatesForReset(false);
      setStateForReset(payload);
    } else { // user clicked 'reset settings'
      setLockUpdatesForReset(true);
      updateLink({
        ...state,
        ...payload,
      })
        .then(() => {
          handleNotify('reset-state');
          setLockUpdatesForReset(false);
        })
        .catch(() => {
          Notify.create({
            title: `${t('Error updating Share Link')}`,
            type: "error"
          });
          closeModal();
        });
    }

    const { expires, require_identification, view_only } = payload;
    if (expires || require_identification || view_only) {
      setShowMoreOptions(true);
    }
  };

  const handleGet = () => {
    getLink(BFG.manifestDigest)
      .then((data) => {
        const payload = {
          ...data.attributes,
          ...{ key: data.id },
        };
        handleReset({ payload });
      })
      .catch(() => closeModal());
  };

  const handleCreate = () => {
    const { key, type } = BFG.resource;
    createLink({
      assetKeys: [...selectedAssetKeys],
      ...state,
      key, // pass after state, to override state values
      type,
      name: defaultName(),
    })
      .then(({ data }) => {
        const payload = {
          ...data.attributes,
          ...{ key: data.id },
        };
        handleReset({ payload });
      })
      .catch(() => closeModal());
  };

  const handleUpdate = ({ type, payload }) => {
    if (isLoading || lockUpdatesForReset) return;

    dispatch({ type, payload }); // update state immediately

    if (type === 'expires') return; // expires toggle udpate is tied to availability_end update, only need to updateLink for 1

    const isBooleanParam = typeof state[type] === 'boolean' || type === 'view_only';
    const isStringParam = type === 'name' || type === 'availability_end';

    updateLink({
      ...state,
      ...isBooleanParam && { [type]: !state[type] }, // update boolean param using current state, NOT new state
      ...isStringParam && { [type]: payload }, // update name or expiration date using argument, NOT new state
    })
      .then(() => {
        handleNotify(type);
        setLockUpdatesForReset(false);
      })
      .catch(() => {
        Notify.create({
          title: `${t('Error updating Share Link')}`,
          type: "error"
        });
        closeModal();
      });
  };

  const handleDelete = () => {
    window.swal(sweetAlertDeleteOptions, (confirm) => {
      if (confirm) {
        deleteLink(state.key)
          .then(() => {
            closeModal();
            Notify.create({
              title: t("Share link deleted"),
              type: "success"
            });
            if (editLinkView) {
              redirectToContextable();
            }
          })
          .catch((err) => console.log(err));
      }
    });
  };

  useEffect(() => {
    if (editLinkView && stateForReset) {
      let changesPresent = false;
      Object.keys(state).forEach((param) => {
        if (state[param] !== stateForReset[param]) {
          changesPresent = true;
        }
      });
      setUnsavedChanges(changesPresent);
    }
  }, [state, stateForReset]);

  useEffect(() => {
    handleUpdate({ type: 'name', payload: debouncedName });
  }, [debouncedName]);

  useEffect(() => {
    handleUpdate({ type: 'availability_end', payload: debouncedExpiration });
  }, [debouncedExpiration]);

  useEffect(() => {
    reportGA();
    selectedAssetKeys.forEach((assetKey) => reportInsight(assetKey));
  }, [selectedAssetKeys]);

  useEffect(() => {
    if (editLinkView) {
      handleGet();
    } else {
      handleCreate();
    }
    return (() => closeModal());
  }, []);

  const isPrivate = state.restrict_shares_setting || state.internal;

  return (
    <div className="modal-content-wrapper bulk-share-modal">
      <div className="modal-content-wrapper__header">
        <span className="bff-share icon" aria-hidden="true" />
        <h3 className="modal-title">
          {isPrivate ? t('Private Share Link for') : t('Public Share Link for')}
          <span className="bold-text asset-qty">{`${assetQtyObject.qty} ${assetCopy}`}</span>
        </h3>
        <button
          className="close-button"
          onClick={closeModal}
          type="button"
        >
          <span className="bff-close" />
        </button>
      </div>
      {isLoading
        ? (
          <BFLoader />
        )
        : (
          <div className="modal-content-wrapper__body">
            <InputContainer
              attributes={{
                name: 'share-link-name',
                onChange: (e) => dispatch({
                  type: 'name',
                  payload: e.target.value,
                }),
                type: 'text',
              }}
              input={{ value: state.name }}
              labelCopy={t('Share Link Name')}
            />
            <div className="share-link-container">
              <InputContainer
                attributes={{
                  name: 'share-link',
                  readOnly: true,
                  type: 'text',
                  value: state.link,
                }}
                input={{ value: state.link }}
                labelCopy={t('Share Link URL')}
              />
              <ClipboardCopy textToCopy={state.link}>
                <button className="button primary sm" type="button">{t('Copy')}</button>
              </ClipboardCopy>
            </div>
            <div className="actions-links">
              <a
                className="actions-links__link"
                href={state.link}
                rel="noopener noreferrer"
                target="_blank"
              >
                <h5 className="action-text">{t('Open in new tab')}</h5>
              </a>
              <a
                className="actions-links__link"
                href={`mailto:?&subject=link%20to%20${state.name}&body=${state.link}`}
              >
                <h5 className="action-text">{t('Email link')}</h5>
              </a>
            </div>
            {!state.restrict_shares_setting && (BFG.resource.type !== 'organization') && (
              <ToggleContainer
                handleUpdate={handleUpdate}
                param="internal"
                state={state}
              />
            )}
            <CSSTransition
              classNames="more-options"
              in={showMoreOptions}
              timeout={showOptionsTransitionTime}
            >
              <div className="more-options-container">
                <ToggleContainer
                  handleUpdate={handleUpdate}
                  param="require_identification"
                  state={state}
                />
                <ToggleContainer
                  handleUpdate={handleUpdate}
                  param="view_only"
                  state={state}
                />
                <ToggleContainer
                  handleUpdate={handleUpdate}
                  param="expires"
                  state={state}
                />
                <CSSTransition
                  in={state.expires}
                  timeout={showOptionsTransitionTime}
                  unmountOnExit
                >
                  <div className="date-time-picker-transition">
                    <DateTimePicker
                      onChange={(timestamp) => dispatch({
                        type: 'availability_end',
                        payload: timestamp,
                      })}
                      timestamp={state.availability_end || defaultExpiration}
                    />
                  </div>
                </CSSTransition>
              </div>
            </CSSTransition>
            <div className="show-more-options">
              <button
                className="show-more-options--button"
                onClick={() => setShowMoreOptions((prevState) => (!prevState))}
                type="button"
              >
                <span
                  className={`icon ${showMoreOptions ? 'bff-caret-up' : 'bff-caret-down'}`}
                />
                <h5 className="options-copy">
                  {showMoreOptions ? t('Less Options') : t('More Options')}
                </h5>
              </button>
            </div>
          </div>
        )}
      <div className="button-container share-modal-button-container">
        <button
          className="share-modal-delete-button"
          onClick={handleDelete}
          type="button"
        >
          <span className="bff-trash" />
          {t('Delete link')}
        </button>
        {editLinkView && (
          <button
            className={`reset-button ${unsavedChanges ? 'active' : ''}`}
            onClick={() => handleReset({ payload: stateForReset, disableFetchUpdate: false })}
            type="button"
          >
            {t('Restore settings')}
          </button>
        )}
      </div>
    </div>
  );
};

BulkShareModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
};

const ModalComponent = renderModal(BulkShareModal, 'BulkShareModal');
export default ModalComponent;
